// CAMINHO: src/pages/Profile.tsx

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, ChevronRight, FileText, KeyRound, Loader2, Mail, Pencil, Phone, Scale, ShieldCheck } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { EditProfileModal } from '@/components/EditProfileModal';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';

const LOGO = '/imgs/logos allana/letra_A_logo.png';

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  appointment_type?: string | null;
}
interface Doc {
  id: string;
  title: string;
  created_at: string;
}

const ProfilePage = () => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [stats, setStats] = useState({ judicial: 0, administrative: 0, appointments: 0, clients: 0 });
  const [agenda, setAgenda] = useState<Appointment[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [judicial, administrative, appointments, clients, agendaRes, docsRes] = await Promise.all([
        supabase.from('cases').select('id', { count: 'exact', head: true }).eq('user_id', user.id).not('status', 'in', '("Transitado em julgado","Arquivado","Suspenso")').is('archived', false),
        supabase.from('administrative_processes').select('id', { count: 'exact', head: true }).eq('user_id', user.id).not('status', 'in', '("Fechado","Concluído","Arquivado")'),
        supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('clients').select('id', { count: 'exact', head: true }),
        supabase
          .from('appointments')
          .select('id, title, appointment_date, appointment_type')
          .eq('user_id', user.id)
          .gte('appointment_date', format(new Date(), 'yyyy-MM-dd'))
          .order('appointment_date', { ascending: true })
          .limit(4),
        supabase.from('documents').select('id, title, created_at').eq('user_id', user.id).order('created_at', { ascending: false }).limit(4),
      ]);
      setStats({
        judicial: judicial.count || 0,
        administrative: administrative.count || 0,
        appointments: appointments.count || 0,
        clients: clients.count || 0,
      });
      setAgenda((agendaRes.data as Appointment[]) || []);
      setDocs((docsRes.data as Doc[]) || []);
    } catch (error) {
      console.error('Erro ao carregar o perfil:', error);
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#c4ab83]" />
      </div>
    );
  }

  const name = profile?.full_name || 'Usuária';
  const areas = (profile?.specialization || []).filter(Boolean);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();

  const info = [
    { label: 'Email', value: user?.email, icon: Mail },
    { label: 'Telefone', value: profile?.phone, icon: Phone },
    { label: 'OAB', value: profile?.oab_number, icon: ShieldCheck },
    { label: 'Área de atuação', value: areas.join(', '), icon: Scale },
  ];

  const kpis = [
    { label: 'Processos judiciais', value: stats.judicial, hint: 'ativos' },
    { label: 'Administrativos', value: stats.administrative, hint: 'ativos' },
    { label: 'Total de processos', value: stats.judicial + stats.administrative, hint: 'em acompanhamento' },
    { label: 'Clientes', value: stats.clients, hint: 'cadastrados' },
    { label: 'Atendimentos', value: stats.appointments, hint: 'na agenda' },
  ];

  const dayLabel = (iso: string) => {
    try {
      const d = parseISO(iso);
      if (isToday(d)) return 'Hoje';
      if (isTomorrow(d)) return 'Amanhã';
      return format(d, "EEEE", { locale: ptBR });
    } catch {
      return '';
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-4 md:p-6">
      {/* Cartão principal */}
      <section className="relative overflow-hidden rounded-2xl border border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] animate-in fade-in slide-in-from-bottom-1 duration-300">
        <img
          src={LOGO}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-8 top-1/2 h-72 w-72 -translate-y-1/2 select-none object-contain opacity-[0.08]"
        />
        <div className="relative flex flex-col gap-6 p-6 sm:p-8 md:flex-row md:items-center">
          <div className="rounded-full bg-gradient-to-br from-[#e8d5a8] via-[#c4ab83] to-[#9a7f52] p-[3px] shadow-[0_14px_30px_-16px_rgba(122,100,64,0.7)] w-fit">
            <Avatar className="h-28 w-28 border-4 border-white sm:h-32 sm:w-32">
              <AvatarImage src={profile?.avatar_url || ''} alt={name} className="object-cover" />
              <AvatarFallback className="bg-[#faf7f2] text-3xl font-semibold text-[#9a7f52]">{initials}</AvatarFallback>
            </Avatar>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Meu perfil</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Dra. {name}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Advogada{profile?.oab_number ? ` · OAB ${profile.oab_number}` : ''}
            </p>
            {areas.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {areas.map(area => (
                  <span key={area} className="rounded-full bg-[#c4ab83]/15 px-2.5 py-0.5 text-[12px] font-medium text-[#7a6440]">{area}</span>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 md:flex-col md:items-stretch">
            <Button onClick={() => setIsEditModalOpen(true)} className="h-10 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800">
              <Pencil className="mr-2 h-4 w-4 text-[#e8d5a8]" />
              Editar perfil
            </Button>
            <Button variant="outline" onClick={() => setIsPasswordModalOpen(true)} className="h-10 rounded-xl border-slate-200 bg-white text-[13px] text-slate-700 shadow-none">
              <KeyRound className="mr-2 h-4 w-4 text-[#9a7f52]" />
              Alterar senha
            </Button>
          </div>
        </div>

        {/* Dados de contato */}
        <div className="relative grid grid-cols-1 gap-px border-t border-[#c4ab83]/20 bg-[#c4ab83]/15 sm:grid-cols-2 lg:grid-cols-4">
          {info.map(item => (
            <div key={item.label} className="flex items-center gap-3 bg-white/80 px-6 py-4">
              <item.icon className="h-4 w-4 shrink-0 text-[#c4ab83]" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                {item.value ? (
                  <p className="truncate text-sm font-medium text-slate-800">{item.value}</p>
                ) : (
                  <button type="button" onClick={() => setIsEditModalOpen(true)} className="text-sm text-slate-400 hover:text-[#9a7f52]">
                    Adicionar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Números */}
      <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="grid grid-cols-2 gap-px bg-slate-100 md:grid-cols-5">
          {kpis.map(kpi => (
            <div key={kpi.label} className="bg-white px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{kpi.label}</p>
              <p className={`mt-1 text-3xl font-semibold tabular-nums tracking-tight ${kpi.value ? 'text-slate-900' : 'text-slate-300'}`}>
                {loadingData ? '—' : kpi.value}
              </p>
              <p className="text-[11px] text-slate-400">{kpi.hint}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Agenda */}
        <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Agenda</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Próximos compromissos</p>
            </div>
            <button type="button" onClick={() => navigate('/agenda')} className="flex items-center gap-0.5 text-xs font-semibold text-[#9a7f52] hover:underline">
              Ver agenda <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {agenda.length === 0 ? (
            <div className="py-10 text-center">
              <Calendar className="mx-auto h-5 w-5 text-[#c4ab83]" />
              <p className="mt-2 text-sm text-slate-400">Nenhum compromisso pela frente.</p>
            </div>
          ) : (
            <ul className="mt-4 space-y-2">
              {agenda.map(item => {
                const d = parseISO(item.appointment_date);
                return (
                  <li key={item.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#faf7f2]/70">
                    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl border border-[#c4ab83]/30 bg-[#faf7f2]">
                      <span className="text-[10px] font-semibold uppercase leading-none text-[#9a7f52]">{format(d, 'MMM', { locale: ptBR })}</span>
                      <span className="text-lg font-semibold leading-tight tabular-nums text-slate-900">{format(d, 'dd')}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{item.title}</p>
                      <p className="text-xs capitalize text-slate-500">
                        {dayLabel(item.appointment_date)}
                        {item.appointment_date.length > 10 ? ` · ${format(d, 'HH:mm')}` : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Documentos */}
        <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Arquivo</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Documentos recentes</p>
            </div>
            <button type="button" onClick={() => navigate('/documents')} className="flex items-center gap-0.5 text-xs font-semibold text-[#9a7f52] hover:underline">
              Ver todos <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {docs.length === 0 ? (
            <div className="py-10 text-center">
              <FileText className="mx-auto h-5 w-5 text-[#c4ab83]" />
              <p className="mt-2 text-sm text-slate-400">Nenhum documento recente.</p>
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {docs.map(doc => (
                <li key={doc.id} className="flex items-center gap-3 py-2.5">
                  <FileText className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{doc.title}</span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-400">{format(parseISO(doc.created_at), 'dd/MM/yyyy')}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <EditProfileModal isOpen={isEditModalOpen} onOpenChange={setIsEditModalOpen} />
      <ChangePasswordModal isOpen={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen} />
    </div>
  );
};

export default ProfilePage;
